/**
 * Sentry — browser runtime.
 *
 * Next.js loads this file into the client bundle itself; there is no import to
 * add anywhere. It catches what the server never sees: a component that throws
 * mid-render, a fetch that rejects, the quote form blowing up in someone's
 * hands.
 *
 * NEXT_PUBLIC_SENTRY_DSN is read at BUILD time, not run time — Next inlines
 * NEXT_PUBLIC_* into the bundle. Setting it in Railway after a deploy does
 * nothing until the next build.
 *
 * No session replay here. PostHog already records sessions on this site, and
 * two recorders means two copies of every customer's screen, twice the payload
 * on a shop-floor tablet, and two places to check. Sentry gets the errors;
 * PostHog keeps the replay.
 */
;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/account/pay/:id\",\"regex\":\"^/account/pay/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/account/pay/:id/complete\",\"regex\":\"^/account/pay/([^/]+)/complete$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/crm/:id\",\"regex\":\"^/crm/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/docs/:slug*?\",\"regex\":\"^/docs(?:/(.*))?$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/pay/:token\",\"regex\":\"^/pay/([^/]+)$\",\"paramNames\":[\"token\"],\"hasOptionalPrefix\":false},{\"path\":\"/portfolio/:slug\",\"regex\":\"^/portfolio/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/services/:slug\",\"regex\":\"^/services/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/about\"},{\"path\":\"/account\"},{\"path\":\"/contact-us\"},{\"path\":\"/crm\"},{\"path\":\"/dashboard\"},{\"path\":\"/dashboard/admin\"},{\"path\":\"/dashboard/admin/analytics\"},{\"path\":\"/dashboard/admin/content\"},{\"path\":\"/dashboard/admin/customers\"},{\"path\":\"/dashboard/admin/draft\"},{\"path\":\"/dashboard/admin/inquiries\"},{\"path\":\"/dashboard/admin/integrations\"},{\"path\":\"/dashboard/admin/orders\"},{\"path\":\"/dashboard/admin/users\"},{\"path\":\"/dashboard/customers\"},{\"path\":\"/dashboard/inventory\"},{\"path\":\"/dashboard/invoicing\"},{\"path\":\"/dashboard/overview\"},{\"path\":\"/dashboard/production\"},{\"path\":\"/dashboard/sales\"},{\"path\":\"/dashboard/system\"},{\"path\":\"/designer\"},{\"path\":\"/developers\"},{\"path\":\"/escalation/respond\"},{\"path\":\"/login\"},{\"path\":\"/order\"},{\"path\":\"/order/apparel\"},{\"path\":\"/order/artwork\"},{\"path\":\"/payment/failed\"},{\"path\":\"/payment/success\"},{\"path\":\"/portfolio\"},{\"path\":\"/pricing\"},{\"path\":\"/privacy\"},{\"path\":\"/privacy/request\"},{\"path\":\"/quote\"},{\"path\":\"/security\"},{\"path\":\"/services\"},{\"path\":\"/staff\"},{\"path\":\"/staff/board\"},{\"path\":\"/staff/intake\"},{\"path\":\"/staff/invoices\"},{\"path\":\"/staff/login\"},{\"path\":\"/terms\"}],\"isrRoutes\":[\"/portfolio/:slug\",\"/services/:slug\"]}";globalThis["_sentryNextJsVersion"] = "16.3.5";globalThis["_sentryRewritesTunnelPath"] = "/monitoring";import * as Sentry from "@sentry/nextjs";

const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;

if (!dsn) {
  // Browser-side, this is the ONLY way to tell the difference between "not
  // configured" and "configured but not firing" — and it is the likelier of
  // the two to be wrong, because NEXT_PUBLIC_* is baked in at build time. A
  // value added to Railway after the last deploy is not in this bundle.
  console.warn(
    "[sentry] browser reporting DISABLED — NEXT_PUBLIC_SENTRY_DSN was not set when this build ran",
  );
}

/**
 * Errors thrown by somebody else's JavaScript inside our page.
 *
 * A visitor who taps an ad in the Facebook or Instagram app does not get
 * Safari: they get Meta's in-app browser, which INJECTS its own script into
 * whatever page it shows. That script talks to the native app over the iOS
 * WKWebView bridge, and on pagehide it reaches for
 * window.webkit.messageHandlers — which is not always there. It throws, our
 * Sentry catches it because it happened in our window, and the stack is
 * attributed to the page URL (app:///designer) as though we wrote it.
 *
 * We did not, and there is nothing to fix: no customer sees anything, and the
 * page works. But every ad click to /designer from Facebook files one, which
 * buries real errors and pages people for nothing. TikTok's and Instagram's
 * browsers do the same thing under different function names.
 *
 * Scoped to the exact symptom rather than a blanket "ignore TypeErrors", so a
 * real null-dereference of ours still gets through.
 */
const THIRD_PARTY_IN_APP_BROWSER = [
  // Meta's in-app browser bridge (iOS).
  /window\.webkit\.messageHandlers/,
  /sendDataToNative/,
  /sendPageHideMessage/,
  // Chrome/Android WebView equivalents seen from the same traffic.
  /webkit\.messageHandlers/,
];

if (dsn) {
  Sentry.init({
    dsn,

    // Injected third-party scripts, not ours. See above.
    ignoreErrors: THIRD_PARTY_IN_APP_BROWSER,

    // Same reasoning as the server config: no automatic PII. Customers type
    // names, emails and phone numbers into the order form, and none of that
    // needs to leave for an error report to be useful.

    tracesSampleRate: process.env.NODE_ENV === "development" ? 1.0 : 0.1,

    enableLogs: true,

    // No manual `release`: the build plugin injects the one it uploaded source
    // maps under, and overriding it with a different string is how a minified
    // production stack trace stops resolving.
  });
}

// Ties client-side navigations into the trace. Exported unconditionally —
// Next.js calls it whether or not a DSN was set, and without an init it is a
// no-op.
export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
